import { createContext, useContext, useState, useEffect } from "react";
import { appApi, appTokenStorage } from "@/lib/appApi";
import { esquecerAcesso, idadeTokenHoras } from "@/lib/appCredenciais";
import SairDialog from "@/components/app/SairDialog";

const Ctx = createContext();
export const useAppAuth = () => useContext(Ctx);
const SESSAO_KEY = "digiponto_app_session";

export function AppAuthProvider({ children }) {
  const [session, setSession] = useState(null); // { app_user, funcionario, empresa }
  const [loading, setLoading] = useState(true);
  const [locked, setLocked] = useState(false);
  const [pedindoSaida, setPedindoSaida] = useState(false);

  // Sessão deslizante: com mais de 1 dia de uso, troca o token por um novo (sem pedir senha)
  const renovar = async () => {
    if (idadeTokenHoras(appTokenStorage.get()) < 24) return;
    try {
      const r = await appApi.post("/app/auth/renovar");
      if (r.data?.token) appTokenStorage.set(r.data.token);
    } catch (_) { /* tenta na próxima abertura */ }
  };

  const refresh = async () => {
    try {
      const r = await appApi.get("/app/me");
      setSession(r.data);
      try { localStorage.setItem(SESSAO_KEY, JSON.stringify(r.data)); } catch (_) { /* sem espaço */ }
      renovar();
      return r.data;
    } catch (e) {
      // Sem internet: mantém a última sessão para permitir registrar ponto offline
      if (!e.response) {
        try {
          const c = JSON.parse(localStorage.getItem(SESSAO_KEY) || "null");
          if (c) { setSession(c); return c; }
        } catch (_) { /* cache inválido */ }
      }
      appTokenStorage.clear();
      localStorage.removeItem(SESSAO_KEY);
      setSession(null);
      return null;
    }
  };

  useEffect(() => {
    const t = appTokenStorage.get();
    if (!t) { setLoading(false); return; }
    // Lazy-import evita ciclo
    import("@/lib/appPin").then(({ appPin }) => {
      refresh().then((s) => {
        import("@/lib/appBio").then(({ appBio }) => {
          if (appPin.isSet() || (s && appBio.isSet(s.app_user?.id))) setLocked(true);
          setLoading(false);
        });
      }).catch(() => setLoading(false));
    });
  }, []);

  const login = async (username, password, opts = {}) => {
    const payload = { username, password };
    if (opts.device_id) payload.device_id = opts.device_id;
    if (opts.device_info) payload.device_info = opts.device_info;
    const r = await appApi.post("/app/auth/login", payload);
    appTokenStorage.set(r.data.token);
    await refresh();
    setLocked(false);  // login completo não precisa de unlock
  };

  const logout = () => {
    appTokenStorage.clear();
    localStorage.removeItem(SESSAO_KEY);
    setSession(null);
    setLocked(false);
  };

  // Saída voluntária: o funcionário escolhe manter ou apagar os dados de acesso salvos
  const sair = async (apagar) => {
    if (apagar) await esquecerAcesso();
    import("@/lib/appPin").then(({ appPin }) => appPin.clear());
    import("@/lib/appBio").then(({ appBio }) => appBio.clear());
    logout();
    setPedindoSaida(false);
  };

  const unlock = () => setLocked(false);

  return (
    <Ctx.Provider value={{ session, loading, locked, login, logout, refresh, unlock, pedirSaida: () => setPedindoSaida(true) }}>
      {children}
      {pedindoSaida && <SairDialog session={session} onSair={sair} onCancelar={() => setPedindoSaida(false)} />}
    </Ctx.Provider>
  );
}
